import React, { useState, useEffect } from 'react';
import { ShieldCheck, Lock, Mail, Key, AlertTriangle, ArrowRight, CheckCircle, RefreshCw, Loader2, ArrowLeft } from 'lucide-react';
import { auth, signInWithEmailAndPassword, db, doc, getDoc } from '../firebase';

interface SellerAdminGateProps {
  currentUser: any;
  onSuccess: (user: any) => void;
  onNavigateHome: () => void;
}

export const SellerAdminGate: React.FC<SellerAdminGateProps> = ({
  currentUser,
  onSuccess,
  onNavigateHome
}) => {
  const [step, setStep] = useState<'login' | 'otp'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [sentOtp, setSentOtp] = useState('');
  const [timer, setTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const targetAdminEmail = 'giffarine108037654@gmail.com';

  // If the user is already authenticated in Firebase with the correct email but has not done OTP yet
  useEffect(() => {
    if (currentUser) {
      const currentEmail = currentUser.email?.toLowerCase().trim();
      if (currentEmail === targetAdminEmail) {
        // If they already completed OTP in this session, skip to success
        const isVerified = sessionStorage.getItem('seller_otp_verified') === 'true';
        if (isVerified) {
          onSuccess(currentUser);
        } else {
          // Trigger OTP step
          setStep('otp');
          generateAndSendOtp();
        }
      } else {
        setError(`การเข้าถึงถูกปฏิเสธ: จำกัดสิทธิ์เฉพาะบัญชี ${targetAdminEmail} เท่านั้น`);
      }
    }
  }, [currentUser]);

  // Countdown timer for OTP
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const generateAndSendOtp = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/seller/otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetAdminEmail })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'ไม่สามารถส่งรหัส OTP ได้');
      }
      setTimer(60);
      setSuccessMsg(`รหัส OTP จริงได้ถูกส่งไปยังอีเมลของคุณแล้ว กรุณาตรวจสอบใน Inbox หรือกล่องจดหมายขยะ (Spam)`);
      setTimeout(() => setSuccessMsg(''), 10000);
    } catch (err: any) {
      setError(err.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์เพื่อส่ง OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const cleanEmail = email.trim().toLowerCase();
    
    // Strict restriction on the target admin email
    if (cleanEmail !== targetAdminEmail) {
      setError(`สิทธิ์ไม่ถูกต้อง: ระบบนี้เข้าดูได้เฉพาะอีเมล ${targetAdminEmail} เท่านั้น`);
      setLoading(false);
      return;
    }

    try {
      // 1. Authenticate with Firebase
      await signInWithEmailAndPassword(auth, cleanEmail, password);
      setSuccessMsg('ยืนยันรหัสผ่านสำเร็จ กำลังสร้างและจัดส่ง OTP จริง...');
      
      // 2. Transition to OTP verification step
      setStep('otp');
      
      // 3. Request real OTP via Backend SMTP
      const res = await fetch('/api/seller/otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetAdminEmail })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'ไม่สามารถส่งรหัส OTP ได้');
      }
      setTimer(60);
      setSuccessMsg(`รหัส OTP จริงถูกจัดส่งไปยังกล่องจดหมายอีเมลของคุณเรียบร้อยแล้ว`);
      setTimeout(() => setSuccessMsg(''), 10000);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'รหัสผ่านไม่ถูกต้อง หรือเกิดข้อผิดพลาดในการตรวจสอบสิทธิ์');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Keep only the last character
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Auto-focus previous input on backspace if empty
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
        const newOtp = [...otp];
        newOtp[index - 1] = '';
        setOtp(newOtp);
      }
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const enteredOtp = otp.join('');
    if (enteredOtp.length !== 6) {
      setError('กรุณากรอกรหัส OTP ให้ครบ 6 หลัก');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/seller/otp/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetAdminEmail, otp: enteredOtp })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'รหัส OTP ไม่ถูกต้อง');
      }

      setSuccessMsg('เข้าสู่ระบบหลังบ้าน Seller Center สำเร็จแล้ว!');
      sessionStorage.setItem('seller_otp_verified', 'true');
      
      setTimeout(() => {
        setLoading(false);
        onSuccess(currentUser || { email: targetAdminEmail, role: 'admin' });
      }, 1000);
    } catch (err: any) {
      setError(err.message || 'รหัส OTP ไม่ถูกต้อง หรือหมดอายุแล้ว');
      setLoading(false);
    }
  };

  const handleResetToLogin = () => {
    setStep('login');
    setError('');
    setSuccessMsg('');
    setOtp(['', '', '', '', '', '']);
  };

  return (
    <div className="min-h-screen w-full bg-[radial-gradient(ellipse_at_top,_#0e2145_0%,_#050b18_100%)] flex flex-col items-center justify-center p-4 font-['Prompt',sans-serif] relative overflow-hidden select-none">
      
      {/* Absolute Specular Background Light Flares */}
      <div className="absolute top-[-20%] left-[-20%] w-[60%] h-[60%] bg-blue-500/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-[-20%] right-[-20%] w-[60%] h-[60%] bg-purple-500/10 blur-[120px] rounded-full pointer-events-none" />

      {/* Silver Dome Glassmorphic Container (กรอบครอบแก้วสีเงินเมทัลลิก) */}
      <div className="relative w-full max-w-md rounded-[32px] bg-white/5 backdrop-blur-2xl border border-white/10 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)] p-6 sm:p-8 overflow-hidden transition-all duration-300">
        
        {/* Upper Highlight Sheen */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-300/40 to-transparent" />
        
        {/* Header Branding */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-blue-600 via-[#2A59FE] to-indigo-500 p-0.5 shadow-xl shadow-blue-500/20 mb-4 animate-fadeIn flex items-center justify-center">
            <div className="w-full h-full bg-slate-950 rounded-[14px] flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-blue-400 animate-pulse" />
            </div>
          </div>
          <h2 className="text-xl font-black text-white tracking-tight flex items-center gap-2">
            <span>GIFFARINE</span>
            <span className="text-blue-400">Seller Center</span>
          </h2>
          <p className="text-[11px] text-blue-300/60 font-medium mt-1 tracking-wider uppercase">
            ระบบความปลอดภัยขั้นสูงสุดระดับธนาคาร
          </p>
        </div>

        {/* Dynamic Step Display */}
        {step === 'login' ? (
          <form onSubmit={handleLoginSubmit} className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-300 flex items-center gap-1.5 pl-1">
                <Mail className="w-3.5 h-3.5 text-blue-400" />
                <span>อีเมลผู้ดูแลที่ได้รับสิทธิ์</span>
              </label>
              <input
                type="email"
                required
                placeholder=""
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-slate-950/80 border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all font-medium placeholder:text-slate-600"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-300 flex items-center gap-1.5 pl-1">
                <Lock className="w-3.5 h-3.5 text-blue-400" />
                <span>รหัสผ่านความปลอดภัย</span>
              </label>
              <input
                type="password"
                required
                placeholder="กรอกรหัสผ่านเพื่อล็อกอิน"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-slate-950/80 border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all font-medium placeholder:text-slate-600"
              />
            </div>

            {/* Notifications / Alerts */}
            {error && (
              <div className="p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs font-bold flex items-center gap-2 animate-fadeIn">
                <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {successMsg && (
              <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-bold flex items-center gap-2 animate-fadeIn">
                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                <span>{successMsg}</span>
              </div>
            )}

            {/* Buttons */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-gradient-to-r from-blue-600 via-[#2A59FE] to-indigo-600 hover:opacity-95 text-white text-xs font-bold rounded-xl shadow-lg shadow-blue-500/15 flex items-center justify-center gap-2 cursor-pointer transition-transform active:scale-[0.98] disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                  <span>กำลังตรวจสอบสิทธิ์...</span>
                </>
              ) : (
                <>
                  <span>ยืนยันข้อมูล & ขอรหัส OTP</span>
                  <ArrowRight className="w-4 h-4 text-blue-200" />
                </>
              )}
            </button>
          </form>
        ) : (
          /* Step 2: 6-Digit Email OTP Gate */
          <form onSubmit={handleVerifyOtp} className="space-y-5">
            <div className="text-center space-y-1">
              <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-500/15 text-blue-300 border border-blue-400/20 text-[11px] font-bold">
                <Key className="w-3.5 h-3.5" />
                <span>ระบบรักษาความปลอดภัย 2 ชั้น (MFA)</span>
              </div>
              <p className="text-xs text-slate-300 font-medium mt-2">
                กรุณากรอกรหัส OTP 6 หลักที่ส่งไปยังอีเมลของคุณ
              </p>
            </div>

            {/* 6 OTP Input Boxes */}
            <div className="flex justify-between gap-2.5 max-w-xs mx-auto">
              {otp.map((digit, idx) => (
                <input
                  key={idx}
                  id={`otp-input-${idx}`}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(idx, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                  className="w-11 h-12 text-center text-lg font-black bg-slate-950/90 border border-white/10 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 rounded-xl focus:outline-none transition-all"
                />
              ))}
            </div>

            {/* Resend OTP Section with Timer */}
            <div className="text-center">
              {timer > 0 ? (
                <p className="text-[11px] text-slate-500 font-medium">
                  ส่งรหัสใหม่อีกครั้งใน <span className="text-blue-400 font-bold">{timer} วินาที</span>
                </p>
              ) : (
                <button
                  type="button"
                  onClick={generateAndSendOtp}
                  className="text-[11px] text-[#2A59FE] hover:text-blue-400 font-bold flex items-center gap-1.5 mx-auto hover:underline cursor-pointer"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>ขอรหัส OTP ใหม่อีกครั้ง</span>
                </button>
              )}
            </div>

            {/* Alerts */}
            {error && (
              <div className="p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs font-bold flex items-center gap-2 animate-fadeIn">
                <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {successMsg && (
              <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-bold flex items-center gap-2 animate-fadeIn">
                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 animate-bounce" />
                <span>{successMsg}</span>
              </div>
            )}

            {/* Buttons */}
            <div className="flex flex-col gap-2.5 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:opacity-95 text-white text-xs font-bold rounded-xl shadow-lg shadow-emerald-500/15 flex items-center justify-center gap-2 cursor-pointer transition-transform active:scale-[0.98]"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-white" />
                    <span>กำลังประมวลผลเซสชัน...</span>
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4 text-emerald-100" />
                    <span>ยืนยันรหัส OTP & เข้าสู่หน้าควบคุม</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleResetToLogin}
                className="w-full py-3 text-slate-400 hover:text-white text-xs font-bold flex items-center justify-center gap-1 hover:underline cursor-pointer"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>ย้อนกลับไปล็อกอินใหม่</span>
              </button>
            </div>
          </form>
        )}

        {/* Back to Home Button */}
        <div className="mt-8 pt-4 border-t border-white/5 flex justify-center">
          <button
            onClick={onNavigateHome}
            className="text-xs text-slate-400 hover:text-blue-300 font-bold flex items-center gap-1 hover:underline cursor-pointer"
          >
            <span>กลับสู่หน้าแรกเว็บไซต์</span>
          </button>
        </div>
      </div>
    </div>
  );
};
